import React, { useState, useEffect } from "react";
import DonutChart from "./DonutChart";
import BubbleChart from "./BubbleChart";
import { fetchKeys, getCbomKeys } from "services/apiServices";
import { CbomKeys } from "@schemas";
import { useProjectContext } from "context/ProjectContext";

const KeysSummary: React.FC = () => {
  const { state } = useProjectContext();
  const [allKeys, setAllKeys] = useState<CbomKeys[]>([]);
  const [keyData, setKeyData] = useState([
    {
      description: "Total No. of Keys",
      value: 0,
    },
    {
      description: "No. of Keys Vulnerable",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Keys Unknown",
      value: 0,
      percent: "",
    },
  ]);
  const [keyTypeData, setKeyTypeData] = useState([
    { name: "Public", noOfItems: 0, color: "custom.public", value: "" },
    {
      name: "Private",
      noOfItems: 0,
      color: "custom.blue3",
      value: "",
    },
    {
      name: "Symmetric",
      noOfItems: 0,
      color: "custom.blue4",
      value: "",
    },
    {
      name: "Hybrid",
      noOfItems: 0,
      color: "custom.blue6",
      value: "",
    },
  ]);
  const [quantumSafeStatusData, setQuantumSafeStatusData] = useState([
    { name: "Unknown", noOfItems: 0, color: "custom.riskverylow", value: "" },
    { name: "Safe", noOfItems: 0, color: "custom.norisk", value: "" },
    {
      name: "Vulnerable",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);
  const [algorithmData, setAlgorithmData] = useState<any[]>([]);

  const formatPercent = (partial: number, total: number) =>
    total > 0 ? `(${((partial / total) * 100).toFixed(0)}%)` : "";

  console.log(allKeys);
  // Random RGBA color generator
  const randomRGBA = () =>
    `rgba(${Math.floor(Math.random() * 256)}, ${Math.floor(
      Math.random() * 256
    )}, ${Math.floor(Math.random() * 256)}, 0.4)`;

  const processKeys = async () => {
    try {
      // const response = await getCbomKeys();

      const keyTypeCounts = allKeys.reduce(
        (acc: any, item: CbomKeys) => {
          if (item.key_type === "Public") acc.Public++;
          else if (item.key_type === "Private") acc.Private++;
          else if (item.key_type === "Symmetric") acc.Symmetric++;
          else if (item.key_type === "Hybrid") acc.Hybrid++;
          return acc;
        },
        { Public: 0, Private: 0, Symmetric: 0, Hybrid: 0 }
      );

      const quantumSafeCounts = allKeys.reduce(
        (acc: any, item: CbomKeys) => {
          if (item.quantum_safe_status?.toLowerCase() === "unknown")
            acc.Unknown++;
          else if (item.quantum_safe_status?.toLowerCase() === "safe")
            acc.Safe++;
          else if (item.quantum_safe_status?.toLowerCase() === "vulnerable")
            acc.Vulnerable++;
          return acc;
        },
        { Unknown: 0, Safe: 0, Vulnerable: 0 }
      );

      console.log(quantumSafeCounts);

      const algorithmCounts = allKeys.reduce((acc: any, item: CbomKeys) => {
        if (item.algorithm) {
          acc[item.algorithm] = (acc[item.algorithm] || 0) + 1;
        }
        return acc;
      }, {});

      const updatedKeyTypeData = [
        {
          name: "Public",
          noOfItems: keyTypeCounts.Public,
          color: "custom.public",
          value: "",
        },
        {
          name: "Private",
          noOfItems: keyTypeCounts.Private,
          color: "custom.blue3",
          value: "",
        },
        {
          name: "Symmetric",
          noOfItems: keyTypeCounts.Symmetric,
          color: "custom.blue4",
          value: "",
        },
        {
          name: "Hybrid",
          noOfItems: keyTypeCounts.Hybrid,
          color: "custom.blue6",
          value: "",
        },
      ];

      const updatedQuantumSafeStatusData = [
        {
          name: "Unknown",
          noOfItems: quantumSafeCounts.Unknown,
          color: "custom.riskverylow",
          value: "",
        },
        {
          name: "Safe",
          noOfItems: quantumSafeCounts.Safe,
          color: "custom.norisk",
          value: "",
        },
        {
          name: "Vulnerable",
          noOfItems: quantumSafeCounts.Vulnerable,
          color: "custom.riskveryhigh",
          value: "",
        },
      ];

      const generatedAlgorithmData = (
        Object.entries(algorithmCounts) as [string, number][]
      ).map(([algorithm, count], index) => ({
        id: index + 1,
        name: `${algorithm}: ${count}`,
        fillColor: randomRGBA(), // Random color generator
        size: Math.max(30, count * 5), // You can adjust bubble sizing logic
        dYdX1: {
          dy: Math.floor(Math.random() * 7) - 3,
          dx: Math.floor(Math.random() * 7) - 3,
        },
      }));

      setKeyData([
        {
          description: "Total No. of Keys",
          value: allKeys.length,
        },
        {
          description: "No. of Keys Vulnerable",
          value: quantumSafeCounts.Vulnerable,
          percent: formatPercent(quantumSafeCounts.Vulnerable, allKeys.length),
        },
        {
          description: "No. of Keys Unknown",
          value: quantumSafeCounts.Unknown,
          percent: formatPercent(quantumSafeCounts.Unknown, allKeys.length),
        },
      ]);
      setKeyTypeData(updatedKeyTypeData);
      setQuantumSafeStatusData(updatedQuantumSafeStatusData);
      setAlgorithmData(generatedAlgorithmData);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchCbomKeys = () => {
      try {
        if (state.currentProjectID) {
          fetchKeys(
            (data) => {
              setAllKeys(data);
            },
            "",
            false,
            state.currentProjectID
          );
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchCbomKeys();
  }, []);

  useEffect(() => {
    processKeys();
  }, [allKeys]);

  return (
    <div className="bg-custom-blue8 rounded-lg px-[10px] py-[20px]">
      <div className="bg-white py-5 rounded-lg shadow-md space-y-5 mb-4">
        {keyData.map((item, index) => (
          <div key={index} className="flex justify-between items-center px-14">
            <p className="w-1/2 text-custom-blue6 text-sm font-normal">
              {item.description}
            </p>
            <p className="w-1/4 text-custom-blue6 text-sm font-semibold">
              {item.value + ` ${item.percent ? item.percent : ""}`}
            </p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-4 w-full">
        <div>
          <DonutChart
            absoluteYearsData={keyTypeData}
            title="Key Type"
            footer=""
          />
        </div>
        <div className="mx-auto p-[10px] bg-white rounded-md shadow-md w-full flex items-center">
          <div className="w-full overflow-auto custom-scrollbar rounded-lg border max-h-[358px]">
            <table
              id="data-table"
              className="min-w-full bg-white shadow rounded-lg"
            >
              <thead className="sticky top-0 z-10 bg-custom-blue0 ">
                <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                  <th className="p-2">S. No</th>
                  <th className="p-2">Key Type</th>
                  <th className="p-2">No. Of Keys</th>
                </tr>
              </thead>
              <tbody>
                {keyTypeData.map((data, index) => (
                  <tr
                    key={index}
                    className={`${
                      index % 2 === 1 ? "bg-custom-blue0" : ""
                    } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                  >
                    <td className="p-2">{index + 1}</td>
                    <td className="p-2">{data.name}</td>
                    <td className="p-2">{data.noOfItems}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <DonutChart
            absoluteYearsData={quantumSafeStatusData}
            title="Quantum Safe Status"
            footer=""
          />
        </div>
        <div>
          {algorithmData.length > 0 && (
            <BubbleChart title="Algorithm" bubbleData={algorithmData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default KeysSummary;
